import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import {
  Bell,
  MessageCircle,
  Mail,
  Clock,
  AlertTriangle,
  Check,
  RefreshCw,
  Loader2,
  ChevronRight,
  Calendar,
  Package
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const CureReminders = ({ language = 'fr' }) => {
  const [reminders, setReminders] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedReminder, setSelectedReminder] = useState(null);
  const [reminderMessage, setReminderMessage] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [sending, setSending] = useState(false);

  const fetchReminders = React.useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('dermascan_token');
      const response = await axios.get(`${API_URL}/cure-followup/due-reminders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReminders(response.data.due_reminders || []);
    } catch (error) {
      console.error('Error fetching reminders:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchStats = React.useCallback(async () => {
    try {
      const token = localStorage.getItem('dermascan_token');
      const response = await axios.get(`${API_URL}/cure-followup/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  }, []);

  useEffect(() => {
    fetchReminders();
    fetchStats();
  }, [fetchReminders, fetchStats]);

  const openReminderDialog = async (reminder) => {
    setSelectedReminder(reminder);
    setSending(true);
    setDialogOpen(true);
    
    try {
      const token = localStorage.getItem('dermascan_token');
      const response = await axios.get(
        `${API_URL}/cure-followup/generate-reminder-message/${reminder.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setReminderMessage(response.data);
    } catch (error) {
      console.error('Error generating message:', error);
      toast.error('Erreur lors de la génération du message');
    } finally {
      setSending(false);
    }
  };

  const sendViaWhatsApp = async () => {
    if (!reminderMessage?.whatsapp?.link) return;
    
    // Open WhatsApp
    window.open(reminderMessage.whatsapp.link, '_blank');
    
    // Mark as reminded
    try {
      const token = localStorage.getItem('dermascan_token');
      await axios.post(
        `${API_URL}/cure-followup/${selectedReminder.id}/mark-reminded?method=whatsapp`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Rappel envoyé via WhatsApp');
      setDialogOpen(false);
      fetchReminders();
      fetchStats();
    } catch (error) {
      console.error('Error marking as reminded:', error);
    }
  };

  const sendViaEmail = async () => {
    if (!reminderMessage?.email || !selectedReminder?.client_email) {
      toast.error('Pas d\'email disponible pour ce client');
      return;
    }
    
    // For now, open mailto link - could be replaced with actual email sending
    const mailtoLink = `mailto:${selectedReminder.client_email}?subject=${encodeURIComponent(reminderMessage.email.subject)}&body=${encodeURIComponent(reminderMessage.email.body)}`;
    window.open(mailtoLink, '_blank');
    
    // Mark as reminded
    try {
      const token = localStorage.getItem('dermascan_token');
      await axios.post(
        `${API_URL}/cure-followup/${selectedReminder.id}/mark-reminded?method=email`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Rappel envoyé par email');
      setDialogOpen(false);
      fetchReminders();
      fetchStats();
    } catch (error) {
      console.error('Error marking as reminded:', error);
    }
  };

  const markAsRenewed = async (reminder) => {
    try {
      const token = localStorage.getItem('dermascan_token');
      await axios.post(
        `${API_URL}/cure-followup/${reminder.id}/mark-renewed`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Cure marquée comme renouvelée');
      fetchReminders();
      fetchStats();
    } catch (error) {
      console.error('Error marking as renewed:', error);
      toast.error('Erreur');
    }
  };

  const getDaysLabel = (days) => {
    if (days < 0) return `Expiré depuis ${Math.abs(days)} jour(s)`;
    if (days === 0) return "Se termine aujourd'hui";
    if (days === 1) return "Se termine demain";
    return `${days} jours restants`;
  };

  const getDaysBadgeColor = (days, isUrgent) => {
    if (days < 0) return "bg-red-100 text-red-800 border-red-200";
    if (isUrgent) return "bg-orange-100 text-orange-800 border-orange-200";
    return "bg-yellow-100 text-yellow-800 border-yellow-200";
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6 flex items-center justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-cyan-500" />
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Stats Summary */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card className="bg-gradient-to-br from-cyan-50 to-cyan-100">
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-cyan-700">{stats.active_cures}</div>
              <div className="text-xs text-cyan-600">Cures actives</div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-orange-50 to-orange-100">
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-orange-700">{stats.due_for_reminder}</div>
              <div className="text-xs text-orange-600">À rappeler</div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-green-50 to-green-100">
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-green-700">{stats.renewed_cures}</div>
              <div className="text-xs text-green-600">Renouvelées</div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-purple-50 to-purple-100">
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-purple-700">{stats.renewal_rate}%</div>
              <div className="text-xs text-purple-600">Taux renouvellement</div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Reminders List */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2">
              <Bell className="w-5 h-5 text-orange-500" />
              {language === 'fr' ? 'Clients à rappeler' : 'Clients to remind'}
              {reminders.length > 0 && (
                <Badge variant="destructive" className="ml-2">{reminders.length}</Badge>
              )}
            </span>
            <Button variant="ghost" size="sm" onClick={fetchReminders}>
              <RefreshCw className="w-4 h-4" />
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {reminders.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Check className="w-12 h-12 mx-auto text-green-500 mb-2" />
              <p>{language === 'fr' ? 'Aucun client à rappeler pour le moment' : 'No clients to remind right now'}</p>
            </div>
          ) : (
            <div className="space-y-3">
              {reminders.map(reminder => (
                <div 
                  key={reminder.id}
                  className={`border rounded-lg p-4 transition-all hover:shadow-md ${
                    reminder.is_expired ? 'border-red-200 bg-red-50' :
                    reminder.is_urgent ? 'border-orange-200 bg-orange-50' :
                    'border-yellow-200 bg-yellow-50'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold">{reminder.client_name}</span>
                        <Badge 
                          variant="outline" 
                          className={getDaysBadgeColor(reminder.days_remaining, reminder.is_urgent)}
                        >
                          {reminder.is_expired && <AlertTriangle className="w-3 h-3 mr-1" />}
                          {getDaysLabel(reminder.days_remaining)}
                        </Badge>
                      </div>
                      <p className="text-sm text-gray-600 mt-1">
                        📞 {reminder.client_phone}
                        {reminder.client_email && ` • 📧 ${reminder.client_email}`}
                      </p>
                      <div className="flex items-center gap-2 mt-2 text-xs text-gray-500">
                        <Calendar className="w-3 h-3" />
                        Fin: {new Date(reminder.end_date).toLocaleDateString('fr-FR')}
                        <span className="mx-1">•</span>
                        <Package className="w-3 h-3" />
                        {reminder.products?.length || 0} produit(s)
                      </div>
                      {reminder.reminder_sent && (
                        <Badge variant="outline" className="mt-2 bg-blue-50 text-blue-700 text-xs">
                          <Check className="w-3 h-3 mr-1" />
                          Rappel déjà envoyé
                        </Badge>
                      )}
                    </div>
                    <div className="flex flex-col gap-2">
                      <Button 
                        size="sm"
                        onClick={() => openReminderDialog(reminder)}
                        className="bg-green-600 hover:bg-green-700"
                      >
                        <MessageCircle className="w-4 h-4 mr-1" />
                        Rappeler
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => markAsRenewed(reminder)}
                        className="text-purple-600 border-purple-300"
                      >
                        <Check className="w-4 h-4 mr-1" />
                        Renouvelé
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Reminder Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bell className="w-5 h-5 text-orange-500" />
              Envoyer un rappel à {selectedReminder?.client_name}
            </DialogTitle>
          </DialogHeader>

          {sending ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-cyan-500" />
            </div>
          ) : reminderMessage ? (
            <div className="space-y-4">
              {/* Client Info */}
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-sm">
                  <strong>Client:</strong> {reminderMessage.client_name}<br/>
                  <strong>Téléphone:</strong> {reminderMessage.client_phone}<br/>
                  {reminderMessage.client_email && (
                    <><strong>Email:</strong> {reminderMessage.client_email}<br/></>
                  )}
                  <strong>Jours restants:</strong> {reminderMessage.days_remaining} jour(s)
                </p>
              </div>

              {/* Message Preview */}
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">
                  Aperçu du message:
                </label>
                <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm whitespace-pre-line max-h-48 overflow-y-auto">
                  {reminderMessage.whatsapp?.message}
                </div>
              </div>

              {/* Send Buttons */}
              <div className="flex gap-3">
                <Button 
                  onClick={sendViaWhatsApp}
                  className="flex-1 bg-green-600 hover:bg-green-700"
                >
                  <MessageCircle className="w-4 h-4 mr-2" />
                  Envoyer via WhatsApp
                </Button>
                {reminderMessage.client_email && (
                  <Button 
                    onClick={sendViaEmail}
                    variant="outline"
                    className="flex-1 border-blue-500 text-blue-600 hover:bg-blue-50"
                  >
                    <Mail className="w-4 h-4 mr-2" />
                    Envoyer par Email
                  </Button>
                )}
              </div>
            </div>
          ) : (
            <div className="text-center py-4 text-red-500">
              Erreur lors de la génération du message
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CureReminders;
